// src/pages/Mypage/PatientDocumentsPage.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { axiosInstance } from '../../utils/axios';
import PageHeader from './PageHeader';
import AlertModal from '../common/AlertModal';
import MypageTargetSwitcher from './MypageTargetSwitcher';
import { FileText, Pill, ClipboardList, Upload, Trash2, ExternalLink, X, Image, File } from 'lucide-react';

const TABS = [
  { key: 'medical', label: '健診', icon: FileText },
  { key: 'medicine_notebook', label: 'お薬', icon: Pill },
  { key: 'order_documents', label: 'オーダー', icon: ClipboardList },
];

const PatientDocumentsPage = () => {
  const [active, setActive] = useState('medical');
  const [items, setItems] = useState([]);
  const [childProfiles, setChildProfiles] = useState([]);
  const [selectedTargetId, setSelectedTargetId] = useState('self');
  const [loading, setLoading] = useState(true);
  const [stagingFiles, setStagingFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [alertModal, setAlertModal] = useState({ open: false, title: '', message: '' });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
  const fileRef = useRef(null);

  const selectedChild = useMemo(() => {
    if (!selectedTargetId.startsWith('child-')) return null;
    const childId = Number(selectedTargetId.replace('child-', ''));
    return childProfiles.find((child) => child.id === childId) || null;
  }, [selectedTargetId, childProfiles]);

  const targets = useMemo(() => ([
    { id: 'self', label: '自分' },
    ...childProfiles.map((child) => ({ id: `child-${child.id}`, label: child.full_name || `${child.last_name || ''} ${child.first_name || ''}`.trim() })),
  ]), [childProfiles]);

  const visibleTabs = useMemo(() => TABS, []);

  const load = async (docType = active) => {
    setLoading(true);
    try {
      const params = { doc_type: docType };
      if (selectedChild?.id) params.child_profile_id = selectedChild.id;
      const { data } = await axiosInstance.get('/api/medichat/patient_documents', { params });
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const { data } = await axiosInstance.get('/api/medichat/child_profiles').catch(() => ({ data: { child_profiles: [] } }));
      setChildProfiles(data?.child_profiles || []);
    })();
  }, []);

  useEffect(() => {
    load(active);
  }, [active, selectedTargetId, selectedChild?.id]);

  const previews = useMemo(() => {
    return stagingFiles.map((f) => {
      const url = URL.createObjectURL(f);
      return { url, name: f.name, type: f.type };
    });
  }, [stagingFiles]);

  useEffect(() => {
    return () => { previews.forEach(p => URL.revokeObjectURL(p.url)); };
  }, [previews]);

  const onPickFiles = (files) => {
    if (!files?.length) return;
    setStagingFiles(prev => [...prev, ...Array.from(files)]);
  };

  const removeStagingAt = (idx) => {
    setStagingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const clearStaging = () => setStagingFiles([]);

  const submitAll = async () => {
    if (!stagingFiles.length || submitting) return;
    setSubmitting(true);
    try {
      for (const file of stagingFiles) {
        const fd = new FormData();
        fd.append('doc_type', active);
        fd.append('file', file);
        if (selectedChild?.id) fd.append('child_profile_id', selectedChild.id);
        await axiosInstance.post('/api/medichat/patient_documents', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      clearStaging();
      await load();
    } catch (e) {
      setAlertModal({ open: true, title: 'エラー', message: e?.response?.data?.error || e?.message || 'アップロードに失敗しました' });
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    const params = selectedChild?.id ? { child_profile_id: selectedChild.id } : {};
    await axiosInstance.delete(`/api/medichat/patient_documents/${id}`, { params });
    setDeleteConfirm({ open: false, id: null });
    await load();
  };

  const getTabDescription = () => {
    switch (active) {
      case 'medical':
        return '健康診断の結果や血液検査の結果を保存できます';
      case 'medicine_notebook':
        return '処方されたお薬の情報を記録・管理できます';
      case 'order_documents':
        return '診断書や証明書などの書類を確認できます';
      default:
        return '';
    }
  };

  return (
    <div className="mc-docs-page">
      <PageHeader title="書類管理" />

      <div className="mc-docs-page__content">
        {/* 対象者切替 */}
        <div className="mc-docs-page__switcher">
          <MypageTargetSwitcher
            targets={targets}
            currentTargetId={selectedTargetId}
            onChange={setSelectedTargetId}
          />
        </div>

        {/* タブナビゲーション */}
        <div className="mc-docs-tabs">
          {visibleTabs.map(t => {
            const IconComponent = t.icon;
            return (
              <button
                key={t.key}
                className={`mc-docs-tabs__item ${active === t.key ? 'mc-docs-tabs__item--active' : ''}`}
                onClick={() => setActive(t.key)}
              >
                <IconComponent size={18} />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>

        {/* タブ説明 */}
        <div className="mc-docs-description">
          {getTabDescription()}
        </div>

        {/* アップロードエリア（オーダー書類以外） */}
        {active !== 'order_documents' && (
          <div className="mc-docs-upload">
            <input
              ref={fileRef}
              type="file"
              multiple
              accept="image/*,application/pdf"
              style={{ display: 'none' }}
              onChange={(e) => onPickFiles(e.target.files)}
            />
            <button 
              className="mc-docs-upload__btn"
              onClick={() => fileRef.current?.click()}
            >
              <Upload size={20} />
              <span>
                {selectedChild 
                  ? `${selectedChild.full_name || 'お子さま'}の書類を追加` 
                  : '書類をアップロード'}
              </span>
            </button>
          </div>
        )}

        {/* ステージングファイル */}
        {previews.length > 0 && (
          <div className="mc-docs-staging">
            <div className="mc-docs-staging__header">
              <span>アップロード予定 ({stagingFiles.length}件)</span>
              <button onClick={clearStaging} className="mc-docs-staging__clear">
                すべて取り消し
              </button>
            </div>
            <div className="mc-docs-staging__list">
              {previews.map((p, i) => {
                const isImg = /^image\//.test(p.type || '');
                return (
                  <div key={p.url} className="mc-docs-staging__item">
                    <div className="mc-docs-staging__preview">
                      {isImg ? (
                        <img src={p.url} alt={p.name} />
                      ) : (
                        <File size={24} />
                      )}
                    </div>
                    <span className="mc-docs-staging__name">{p.name}</span>
                    <button 
                      className="mc-docs-staging__remove"
                      onClick={() => removeStagingAt(i)}
                    >
                      <X size={16} />
                    </button>
                  </div>
                );
              })}
            </div>
            <button
              className="mc-docs-staging__submit"
              disabled={submitting}
              onClick={submitAll}
            >
              {submitting ? '送信中...' : `${stagingFiles.length}件をアップロード`}
            </button>
          </div>
        )}

        {/* メインコンテンツ */}
        <div className="mc-docs-main">
          {loading ? (
            <div className="mc-docs-loading">
              <div className="mc-docs-loading__spinner" />
              <span>読み込み中...</span>
            </div>
          ) : items.length === 0 ? (
            <div className="mc-docs-empty">
              <div className="mc-docs-empty__icon">
                {active === 'medical' && <FileText size={48} />}
                {active === 'medicine_notebook' && <Pill size={48} />}
                {active === 'order_documents' && <ClipboardList size={48} />}
              </div>
              <div className="mc-docs-empty__text">
                {active === 'order_documents' 
                  ? '発行された書類はありません'
                  : '登録された書類はありません'}
              </div>
              {active !== 'order_documents' && (
                <div className="mc-docs-empty__hint">
                  上のボタンから書類を追加できます
                </div>
              )}
            </div>
          ) : active === 'order_documents' ? (
            <div className="mc-docs-list">
              {items.map(it => {
                const dateStr = it.issued_at 
                  ? new Date(it.issued_at).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' })
                  : new Date(it.created_at).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' });
                
                return (
                  <a 
                    key={it.id} 
                    href={it.url} 
                    target="_blank" 
                    rel="noreferrer"
                    className="mc-docs-list__item"
                  >
                    <div className="mc-docs-list__icon">
                      <FileText size={24} />
                    </div>
                    <div className="mc-docs-list__info">
                      <div className="mc-docs-list__title">
                        {it.title || it.doc_type_label || '書類'}
                      </div>
                      <div className="mc-docs-list__meta">
                        {dateStr}
                        {it.disease_name && ` ・ ${it.disease_name}`}
                      </div>
                    </div>
                    <div className="mc-docs-list__action">
                      <ExternalLink size={18} />
                    </div>
                  </a>
                );
              })}
            </div>
          ) : (
            <div className="mc-docs-grid">
              {items.map(it => {
                const isImg = /^image\//.test(it.content_type || '');
                const isPdf = (it.content_type || '') === 'application/pdf';
                const dateStr = it.created_at 
                  ? new Date(it.created_at).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' })
                  : '';
                
                return (
                  <div key={it.id} className="mc-docs-card">
                    <a 
                      href={it.url} 
                      target="_blank" 
                      rel="noreferrer"
                      className="mc-docs-card__preview"
                    >
                      {isImg ? (
                        <img src={it.url} alt={it.filename} />
                      ) : isPdf ? (
                        <div className="mc-docs-card__pdf">
                          <FileText size={32} />
                          <span>PDF</span>
                        </div>
                      ) : (
                        <div className="mc-docs-card__file">
                          <File size={32} />
                        </div>
                      )}
                    </a>
                    <div className="mc-docs-card__footer">
                      <div className="mc-docs-card__info">
                        <span className="mc-docs-card__name" title={it.filename}>
                          {it.filename?.length > 15 ? it.filename.slice(0, 15) + '...' : it.filename}
                        </span>
                        {dateStr && <span className="mc-docs-card__date">{dateStr}</span>}
                      </div>
                      {it.deletable !== false && (
                        <button 
                          className="mc-docs-card__delete"
                          onClick={(e) => {
                            e.preventDefault();
                            setDeleteConfirm({ open: true, id: it.id });
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* 削除確認モーダル */}
      {deleteConfirm.open && (
        <div className="mc-docs-modal-backdrop" onClick={() => setDeleteConfirm({ open: false, id: null })}>
          <div className="mc-docs-modal" onClick={e => e.stopPropagation()}>
            <div className="mc-docs-modal__icon">
              <Trash2 size={32} />
            </div>
            <div className="mc-docs-modal__title">書類を削除しますか？</div>
            <div className="mc-docs-modal__text">この操作は取り消せません</div>
            <div className="mc-docs-modal__actions">
              <button 
                className="mc-docs-modal__btn mc-docs-modal__btn--cancel"
                onClick={() => setDeleteConfirm({ open: false, id: null })}
              >
                キャンセル
              </button>
              <button 
                className="mc-docs-modal__btn mc-docs-modal__btn--danger"
                onClick={() => remove(deleteConfirm.id)}
              >
                削除する
              </button>
            </div>
          </div>
        </div>
      )}
      
      <AlertModal 
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        onClose={() => setAlertModal({ open: false, title: '', message: '' })}
      />
    </div>
  );
};

export default PatientDocumentsPage;
